import {
    ButtonAction,
    ButtonSize,
    ButtonType,
    ButtonWidth,
    IconType,
    SafeAny,
} from '@pf/shared-common';
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    EventEmitter,
    HostBinding,
    input,
    Input,
    Output,
    TemplateRef,
} from '@angular/core';
import {
    NzButtonModule,
    NzButtonShape,
    NzButtonType,
} from 'ng-zorro-antd/button';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { IconModule } from '../../modules/icon';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';

@Component({
    selector: 'pf-button',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NzButtonModule, NzTooltipDirective, IconModule, NzDropDownModule],
})
export class ButtonComponent {
    @Input() tooltip?: string | TemplateRef<void>;
    @Input() buttonActions?: ButtonAction[] | null;
    @Input() actionsOnly = false;
    @Input() buttonSize: ButtonSize = 'default';
    @Input() loading = false;
    @Input() disabled = false;
    @Output() iconClick = new EventEmitter<MouseEvent>();

    @Input() pfDanger = false;

    @Input() actionsContext?: SafeAny;

    @HostBinding('class.active')
    @Input()
    active = false;

    /**
     * The `icon` input will be used to create a button with an icon.
     * Only valid for button type of `icon` or `icon-square`
     */
    pfIcon = input<keyof typeof IconType>();
    buttonType = input<ButtonType>('submit');

    @HostBinding('class.pf-full-width')
    get isFullWidth(): boolean {
        return this.typeProps?.width === 'full';
    }

    @HostBinding('attribute.success')
    get isSuccess(): boolean {
        return !!this.typeProps?.success;
    }

    typeProps?: {
        danger?: boolean;
        type: NzButtonType;
        shape: NzButtonShape;
        icon: keyof typeof IconType | '';
        iconRotate?: number;
        slim: true | null;
        width?: ButtonWidth;
        success?: boolean;
    };
    showActions = false;

    constructor() {
        effect(() => {
            const buttonType = this.buttonType() ?? 'submit';
            const isIconType = this.isIconType(buttonType);

            if (!isIconType || this.pfIcon()) {
                this.setButtonProps(buttonType);
            }
        });
    }

    buttonClickHandler() {
        if (this.actionsOnly && this.buttonActions?.length) {
            this.showActions = true;
        }
    }

    iconClickHandler(event: MouseEvent) {
        this.iconClick.emit(event);
    }

    private isIconType(buttonType: ButtonType) {
        return ['icon', 'icon-square', 'icon-rounded'].includes(buttonType);
    }

    private setButtonProps(buttonType: ButtonType) {
        const config = ButtonConfiguration[buttonType];
        if (!config) {
            throw new Error(`Invalid button type: ${buttonType}`);
        }
        const props = { ...config };
        if (props && this.isIconType(buttonType)) {
            props.icon = this.pfIcon() || '';
        }
        this.typeProps = props as ButtonComponent['typeProps'];
    }
}

const ButtonConfiguration: Record<ButtonType, ButtonComponent['typeProps']> = {
    rounded: {
        type: 'primary',
        icon: '',
        shape: 'round',
        slim: null,
    },
    action: {
        type: 'link',
        icon: '',
        shape: null,
        slim: true,
    },
    'action-add': {
        type: 'link',
        icon: 'plus',
        shape: null,
        slim: true,
    },
    'action-delete': {
        type: 'link',
        icon: '',
        shape: null,
        slim: true,
        danger: true,
    },
    'actions-more': {
        type: 'link',
        icon: 'expand',
        shape: null,
        slim: true,
    },
    add: {
        type: 'primary',
        icon: 'plus',
        shape: null,
        slim: null,
    },
    'add-item': {
        type: 'default',
        icon: 'plus',
        shape: null,
        slim: true,
        width: 'full',
    },
    'add-with-menu': {
        type: 'primary',
        icon: 'expand',
        shape: null,
        slim: null,
    },
    apply: {
        type: 'primary',
        icon: '',
        shape: null,
        slim: null,
    },
    cancel: {
        type: 'default',
        icon: '',
        shape: null,
        slim: null,
    },
    clear: {
        type: 'primary',
        shape: 'round',
        icon: 'close',
        slim: null,
    },
    'clear-square': {
        type: 'default',
        icon: '',
        shape: null,
        slim: null,
    },
    copy: {
        type: 'link',
        icon: 'copy',
        shape: null,
        slim: null,
    },
    delete: {
        type: 'default',
        icon: '',
        shape: null,
        slim: null,
        width: 'full',
        danger: true,
    },
    'full-width': {
        type: 'default',
        icon: '',
        shape: null,
        slim: null,
        width: 'full',
    },
    export: {
        type: 'primary',
        icon: 'export',
        shape: null,
        slim: null,
    },
    icon: {
        type: 'primary',
        icon: '',
        shape: 'circle',
        slim: null,
    },
    'icon-square': {
        type: 'primary',
        icon: '',
        shape: null,
        slim: null,
    },
    'icon-rounded': {
        type: 'primary',
        icon: '',
        shape: 'round',
        slim: null,
    },
    refresh: {
        type: 'primary',
        icon: 'refresh',
        shape: 'round',
        slim: null,
    },
    route: {
        type: 'primary',
        icon: 'route',
        shape: null,
        slim: null,
    },
    search: {
        type: 'default',
        icon: 'search',
        shape: null,
        slim: true,
        width: 'full',
    },
    submit: {
        type: 'primary',
        icon: '',
        shape: null,
        slim: null,
    },
    undo: {
        type: 'default',
        icon: 'undo',
        shape: 'round',
        slim: null,
    },
    upload: {
        type: 'primary',
        icon: 'upload',
        shape: null,
        slim: null,
    },
    view: {
        type: 'primary',
        icon: 'view',
        shape: null,
        slim: null,
    },
    play: {
        type: 'primary',
        icon: 'play',
        shape: null,
        slim: null,
    },
    exception: {
        type: 'default',
        icon: 'expand',
        iconRotate: 180,
        shape: null,
        slim: true,
        danger: true,
    },
    download: {
        type: 'default',
        icon: 'download',
        shape: null,
        slim: null,
    },
    print: {
        type: 'primary',
        icon: 'print',
        shape: null,
        slim: null,
    },
    'print-rounded': {
        type: 'primary',
        icon: 'print',
        shape: 'round',
        slim: null,
    },
    approve: {
        type: 'primary',
        icon: '',
        shape: null,
        slim: null,
        success: true,
    },
    deny: {
        type: 'default',
        danger: true,
        icon: '',
        shape: null,
        slim: null,
    },
};
